import { verifyAdUser } from "api/auth"; // Ensure verifyAdUser is imported or defined
import { addUser } from "api/userManagement";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import EmailSearchInput from "themes/elements/EmailSearchInput";
import InputComp from "themes/elements/Input";
import ModalComp from "themes/elements/Modal";
import SelectComp from "themes/elements/Select";
import { retrieveToken } from "utils/cryptoStorage";
import ModalFooter from "../modalFooter";
import { USER_TYPE } from "./config";

function AddUser({ open, setOpen, fetchUsers }) {
  const { rolesData } = useSelector((state) => state.users);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState(null);
  const [roleValue, setRoleValue] = useState();
  const [loading, setLoading] = useState(false); // Unified loading state

  const [userTypeValue, setUserTypeValue] = useState(USER_TYPE[0]?.value);
  const [userType, setUserType] = useState(USER_TYPE[0]?.label);

  const [loadingSave, setLoadingSave] = useState(false);
  const [loadingInvite, setLoadingInvite] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [verificationData, setVerificationData] = useState(null); // State to store verification response

  const handleSaveEmail = async (email) => {
    setLoading(true); // Start loading when verifying

    setSelectedEmail(email);
    const token = retrieveToken("accessToken");

    try {
      const verificationResult = await verifyAdUser(
        { action: "search", email: email },
        token
      );

      setVerificationData(verificationResult?.data); // Update the verification status state
    } catch (error) {
      setVerificationData(null); // Reset or handle error state
    } finally {
      setLoading(false); // Stop loading after verification
    }
  };

  // Define roleOptions here based on rolesData from Redux
  const roleOptions = rolesData.map((role) => ({
    value: role.role_name,
    id: role.role_id,
    name: role.role_name,
  }));

  const handleFirstNameChange = (event) => setFirstName(event.target.value);
  const handleLastNameChange = (event) => setLastName(event.target.value);
  const handleEmailChange = (event) => {
    setVerificationData(null);
    setEmail(event.target.value);
  };
  const handleRoleChange = (value, item) => {
    setRoleValue(value);
    setRole(item.id);
  };
  const handleUserTypeChange = (value) => {
    setVerificationData(null);

    setUserTypeValue(value);
    setUserType(USER_TYPE.find((type) => type.value === value)?.label);
  };

  const validateEmail = (email) => {
    const re = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    return re.test(String(email).toLowerCase());
  };

  useEffect(() => {
    clearForm();
  }, [open]);

  const validateFields = () => {
    if (userTypeValue === 2) {
      if (!selectedEmail) {
        toast.warning("Please enter the email");
        return false;
      }
      const emailToValidate = userTypeValue === 1 ? email : selectedEmail;
      if (!validateEmail(emailToValidate)) {
        toast.warning("Please enter a valid email address");
        return false;
      }

      if (!verificationData) {
        toast.warning("Please search and find the Active Directory email");
        return false;
      }
      if (!role) {
        toast.warning("Please select a role");
        return false;
      }
    } else if (userTypeValue === 1) {
      if (!firstName || !lastName || !email || !role) {
        toast.warning("All fields are required");
        return false;
      }
      if (!validateEmail(email)) {
        toast.warning("Please enter a valid email address");
        return false;
      }
    }

    return true;
  };

  const clearForm = () => {
    setFirstName("");
    setLastName("");
    setRoleValue(null);
    setEmail("");
    setRole(null);
    setUserType(USER_TYPE[0].label);
    setUserTypeValue(USER_TYPE[0].value);
  };

  const handleSave = async (type) => {
    const setLoading = type === "save" ? setLoadingSave : setLoadingInvite;
    setLoading(true);

    if (!validateFields()) {
      setLoading(false);
      return;
    }

    let roleOption = roleOptions.find((option) => option.id === role);

    // Select first name based on availability and user type
    const firstNameForUser =
      userTypeValue === 2
        ? verificationData?.given_name || verificationData?.display_name
        : firstName;

    // Select last name based on availability and user type
    const lastNameForUser =
      userTypeValue === 2 ? verificationData?.surname || "" : lastName;

    // Construct role data object
    let roleData = {
      user_first_name: firstNameForUser,
      user_last_name: lastNameForUser,
      role_id: roleOption.id,
      email: userTypeValue === 2 ? selectedEmail : email,
      user_type: userTypeValue,
      user_status_flg: 1,
      send_invitation_flg: type === "invite" ? 1 : 0,
    };

    // Add the Azure AD ID if userTypeValue is 2
    if (userTypeValue === 2) {
      roleData.ad_id = verificationData?.azure_ad_id;
    }

    try {
      const res = await addUser(roleData);
      if (res?.message) {
        toast.success(res.message);
        setOpen(false);
        clearForm();
        fetchUsers();
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalComp
      title="Add a new user"
      footer={
        <ModalFooter
          primaryBtnloading={loadingSave ? loadingSave : false}
          secondaryBtnLoading={loadingInvite ? loadingInvite : false}
          handlePrimaryBtn={() => handleSave("save")}
          handleSecondaryBtn={() => handleSave("invite")}
          primaryBtnVariant="secondary"
          primaryBtnText="Save user"
          secondaryBtnText="Send invitation"
        />
      }
      open={open}
      setOpen={setOpen}
    >
      <div className="flex gap-4 flex-column">
        <p className="text-normal text-gray">
          Assigning user roles and access levels to define what this user can
          see and do within the system.
        </p>
        <SelectComp
          defaultValue={userType}
          data={USER_TYPE}
          onChange={handleUserTypeChange}
          label="User type"
          placeholder="Select type"
          value={userTypeValue}
          allowClear={false}
        />

        {userTypeValue === 2 && (
          <EmailSearchInput
            onSave={handleSaveEmail}
            onChange={(value) => setSelectedEmail(value)}
            label="Email address"
          />
        )}

        {userTypeValue !== 2 && (
          <div className="flex align-center gap-4">
            <InputComp
              value={firstName}
              onChange={handleFirstNameChange}
              layoutClass="w-full"
              label="First name"
              placeholder="First name"
              maxLength={25}
              showCount
            />
            <InputComp
              value={lastName}
              onChange={handleLastNameChange}
              layoutClass="w-full"
              label="Last name"
              placeholder="Last name"
              maxLength={25}
              showCount
            />
          </div>
        )}
        {verificationData?.display_name && userTypeValue === 2 && (
          <div className="flex align-center gap-4">
            <InputComp
              value={
                verificationData?.given_name || verificationData?.display_name
              }
              layoutClass="w-full"
              label="First name"
              placeholder="First name"
              disabled={true}
            />
            <InputComp
              value={
                verificationData?.surname ? verificationData?.surname : " "
              }
              layoutClass="w-full"
              label="Last name"
              placeholder="Last name"
              disabled={true}
            />
          </div>
        )}
        {userTypeValue !== 2 && (
          <InputComp
            maxLength={50}
            showCount
            value={email}
            onChange={handleEmailChange}
            label="Email address"
            placeholder="Enter email address"
          />
        )}
        <SelectComp
          allowClear={false}
          onChange={handleRoleChange}
          placeholder="Select role"
          data={roleOptions}
          label="Role"
          value={roleValue}
        />
      </div>
    </ModalComp>
  );
}

export default AddUser;
